import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  Button, TextField, Switch, FormControlLabel, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress, Avatar, Tooltip, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Badge
} from '@mui/material';
import {
  ChildCare as DaycareIcon,
  Settings as SettingsIcon,
  PersonAdd as EnrollIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Schedule as ClockIcon,
  Person as PersonIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Edit as EditIcon,
  NotificationsActive as NotifyIcon,
  StickyNote2 as NoteIcon,
  Send as SendIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  MenuBook as DailyNotesIcon,
  Visibility as AcknowledgeIcon,
  Done as CompleteIcon,
  CheckCircle as CompleteConfirmIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmber as AutoClosedIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { principalAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
const elapsed = (checkIn) => {
  if (!checkIn) return '';
  const mins = Math.floor((Date.now() - new Date(checkIn)) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const today = () => new Date().toISOString().slice(0, 10);
const MOODS = ['Happy', 'Calm', 'Energetic', 'Tired', 'Upset', 'Sick'];
const EMPTY_DAILY_FORM = { student_id: '', update_date: today(), content: '', meals: '', nap: '', mood: '', activities: '' };

const TAB_COLORS = {
  0: '#15803D', // Today's Roster — green
  1: '#B45309', // Care Notes — amber
  2: '#0369A1', // Daily Notes — sky blue
  3: '#4F46E5', // Enrollments — indigo
  4: '#BE185D', // Daycare Student Profile — pink
  5: '#B91C1C', // Outstanding Dues — red
  6: '#475569', // Settings — slate
};

const PRIORITY_COLOR = { urgent: 'error', high: 'warning', normal: 'default' };
const PRIORITY_ICON  = { urgent: <UrgentIcon fontSize="small" />, high: <HighIcon fontSize="small" />, normal: null };
const STATUS_COLOR   = { pending: 'warning', acknowledged: 'info', completed: 'success' };

export default function DaycareManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(location.state?.initialTab ?? 0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings]     = useState({ hourly_rate: 0, is_enabled: true, assigned_teacher_id: '' });
  const [enrollments, setEnrollments] = useState([]);
  const [dues, setDues]               = useState([]);
  const [students, setStudents]       = useState([]);
  const [teachers, setTeachers]       = useState([]);
  const [search, setSearch]           = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [enrollSearch, setEnrollSearch] = useState('');

  // Today's roster (operations tab)
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);

  // Auto-closed sessions needing review (missed checkout)
  const [autoClosed, setAutoClosed] = useState([]);

  // Care notes (operations tab)
  const [careNotes, setCareNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  // Daily notes / diary (operations tab)
  const [dailyNotes, setDailyNotes] = useState([]);
  const [dailyNotesLoading, setDailyNotesLoading] = useState(false);
  const [dailySaving, setDailySaving] = useState(false);
  const [dailyEditId, setDailyEditId] = useState(null);
  const [dailyForm, setDailyForm] = useState(EMPTY_DAILY_FORM);
  const [dailyDeleteId, setDailyDeleteId] = useState(null);

  // Edit session dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm]     = useState({ check_in: '', check_out: '' });

  // Notify parent dialog
  const [notifyDialog, setNotifyDialog] = useState(null);
  const [notifyMsg, setNotifyMsg]       = useState('');
  const [notifySending, setNotifySending] = useState(false);

  // Care note acknowledge/complete
  const [noteActionLoading, setNoteActionLoading] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null); // { note }
  const [completionRemark, setCompletionRemark] = useState('');

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const res = await principalAPI.daycare.getRoster();
      setRoster(res.data.roster || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load daycare roster');
    } finally {
      setRosterLoading(false);
    }
  }, []);

  const loadAutoClosed = useCallback(async () => {
    try {
      const res = await principalAPI.daycare.getAutoClosed();
      setAutoClosed(res.data.sessions || []);
    } catch {
      // Non-critical — don't block the page on this
    }
  }, []);

  const loadCareNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const res = await principalAPI.daycare.getCareNotes();
      setCareNotes(res.data.notes || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load care notes');
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const loadDailyNotes = useCallback(async () => {
    setDailyNotesLoading(true);
    try {
      const res = await principalAPI.daycare.getDailyNotes();
      setDailyNotes(res.data.updates || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load daily notes');
    } finally {
      setDailyNotesLoading(false);
    }
  }, []);

  const handleDailySubmit = async () => {
    if (!dailyForm.student_id || !dailyForm.content.trim()) {
      setError('Student and update content are required');
      return;
    }
    setDailySaving(true); setError(''); setSuccess('');
    try {
      if (dailyEditId) {
        await principalAPI.daycare.editDailyNote(dailyEditId, dailyForm);
        setSuccess('Daily update saved');
        setDailyEditId(null);
      } else {
        await principalAPI.daycare.postDailyNote(dailyForm);
        setSuccess('Daily update posted');
      }
      setDailyForm(EMPTY_DAILY_FORM);
      await loadDailyNotes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save daily update');
    } finally {
      setDailySaving(false);
    }
  };

  const openDailyEdit = (u) => {
    setDailyEditId(u.id);
    setDailyForm({
      student_id:  u.student_id,
      update_date: u.update_date?.slice(0, 10) || today(),
      content:     u.content || '',
      meals:       u.meals || '',
      nap:         u.nap || '',
      mood:        u.mood || '',
      activities:  u.activities || '',
    });
  };

  const handleDailyDelete = async () => {
    setError('');
    try {
      await principalAPI.daycare.deleteDailyNote(dailyDeleteId);
      setDailyDeleteId(null);
      setSuccess('Daily update deleted');
      await loadDailyNotes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete daily update');
    }
  };

  useEffect(() => { loadRoster(); loadAutoClosed(); }, [loadRoster, loadAutoClosed]);

  const handleCheckIn = async (student_id) => {
    setError(''); setSuccess('');
    try {
      await principalAPI.daycare.checkIn({ student_id });
      await loadRoster();
      setSuccess('Student checked in');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check in');
    }
  };

  const handleCheckOut = async (session_id) => {
    setError(''); setSuccess('');
    try {
      await principalAPI.daycare.checkOut({ session_id });
      await loadRoster();
      setSuccess('Student checked out');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check out');
    }
  };

  const handleEditSave = async () => {
    setError('');
    try {
      await principalAPI.daycare.editSession(editDialog.session.id, editForm);
      setEditDialog(null);
      await Promise.all([loadRoster(), loadAutoClosed()]);
      setSuccess('Session updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update session');
    }
  };

  const openEdit = (session) => {
    const toLocal = (ts) => {
      if (!ts) return '';
      const d = new Date(ts);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditForm({ check_in: toLocal(session.check_in), check_out: toLocal(session.check_out) });
    setEditDialog({ session });
  };

  const openNotify = (student) => {
    setNotifyMsg('');
    setNotifyDialog({ student_id: student.student_id, student_name: student.student_name });
  };

  const handleSendNotification = async () => {
    if (!notifyMsg.trim()) return;
    setNotifySending(true);
    setError('');
    try {
      await principalAPI.daycare.notifyParent({ student_id: notifyDialog.student_id, message: notifyMsg.trim() });
      setSuccess(`Parent of ${notifyDialog.student_name} notified successfully`);
      setNotifyDialog(null);
      setNotifyMsg('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to notify parent');
    } finally {
      setNotifySending(false);
    }
  };

  const handleAcknowledge = async (note) => {
    setNoteActionLoading(note.id + '_ack');
    setError('');
    try {
      await principalAPI.daycare.acknowledgeCareNote(note.id);
      await loadCareNotes();
      setSuccess('Note acknowledged');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to acknowledge note');
    } finally {
      setNoteActionLoading(null);
    }
  };

  const openComplete = (note) => {
    setCompletionRemark('');
    setCompleteDialog({ note });
  };

  const handleCompleteSubmit = async () => {
    setNoteActionLoading(completeDialog.note.id + '_complete');
    setError('');
    try {
      await principalAPI.daycare.completeCareNote(completeDialog.note.id, completionRemark);
      setCompleteDialog(null);
      await loadCareNotes();
      setSuccess('Note marked as completed — parent notified');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete note');
    } finally {
      setNoteActionLoading(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, dRes, stRes, tRes] = await Promise.all([
        principalAPI.daycare.getSettings(),
        principalAPI.daycare.listEnrollments(),
        principalAPI.daycare.getDues(),
        principalAPI.getStudents(),
        principalAPI.getTeachers(),
      ]);
      const s = sRes.data.settings;
      setSettings({ ...s, assigned_teacher_id: s.assigned_teacher_id || '' });
      setEnrollments(eRes.data.enrollments);
      setDues(dRes.data.dues);
      setStudents(stRes.data.students || []);
      setTeachers(tRes.data.teachers || tRes.data || []);
    } catch (err) {
      setError('Failed to load daycare data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === 1) loadCareNotes();
    if (tab === 2) loadDailyNotes();
  }, [tab, loadCareNotes, loadDailyNotes]);

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    setError(''); setSuccess('');
    try {
      await principalAPI.daycare.updateSettings({
        hourly_rate: settings.hourly_rate,
        is_enabled: settings.is_enabled,
        assigned_teacher_id: settings.assigned_teacher_id || null,
      });
      setSuccess('Settings saved successfully');
    } catch {
      setError('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleToggleEnrollment = async (id, currentActive) => {
    try {
      await principalAPI.daycare.updateEnrollment(id, { is_active: !currentActive });
      setEnrollments(prev => prev.map(e => e.id === id ? { ...e, is_active: !currentActive } : e));
    } catch {
      setError('Failed to update enrollment');
    }
  };

  const handleEnroll = async (student_id) => {
    try {
      await principalAPI.daycare.enrollStudent({ student_id });
      setEnrollDialog(false);
      await load();
      setSuccess('Student enrolled in daycare');
    } catch {
      setError('Failed to enroll student');
    }
  };

  const careNotesPendingCount = careNotes.filter(n => n.status === 'pending').length;
  const enrolledIds = new Set(enrollments.map(e => e.student_id));
  const filteredEnrollments = enrollments.filter(e =>
    !search || e.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.admission_number?.includes(search)
  );
  const unenrolledStudents = students.filter(s =>
    !enrolledIds.has(s.id) &&
    (!enrollSearch || s.name?.toLowerCase().includes(enrollSearch.toLowerCase()))
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <DaycareIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>Daycare Management</Typography>
            </Box>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/principal/dashboard')}>
              Back to Dashboard
            </Button>
          </Box>

          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          {autoClosed.length > 0 && (
            <Alert severity="warning" icon={<AutoClosedIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {autoClosed.length} check-out{autoClosed.length > 1 ? 's were' : ' was'} missed and auto-closed at 7:00 PM. Please review and correct the times below.
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {autoClosed.map(s => (
                  <Box key={s.id} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">
                      <strong>{s.student_name}</strong> — in {fmtTime(s.check_in)}, auto-closed {fmtTime(s.check_out)}
                    </Typography>
                    <Button startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(s)}>
                      Fix Time
                    </Button>
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
          ) : (
            <>
              <Paper elevation={1} sx={{ mb: 3, p: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    { key: 0, icon: <ActiveIcon fontSize="small" />, label: `Today's Roster (${roster.length})` },
                    {
                      key: 1,
                      icon: careNotesPendingCount > 0
                        ? <Badge badgeContent={careNotesPendingCount} color="error"><NoteIcon fontSize="small" /></Badge>
                        : <NoteIcon fontSize="small" />,
                      label: 'Care Notes',
                    },
                    { key: 2, icon: <DailyNotesIcon fontSize="small" />, label: 'Daily Notes' },
                    { key: 3, icon: <EnrollIcon fontSize="small" />, label: `Enrollments (${enrollments.length})` },
                    { key: 4, icon: <PersonIcon fontSize="small" />, label: 'Daycare Student Profile' },
                    { key: 5, icon: <MoneyIcon fontSize="small" />, label: 'Outstanding Dues' },
                    { key: 6, icon: <SettingsIcon fontSize="small" />, label: 'Settings' },
                  ].map(item => {
                    const color = TAB_COLORS[item.key];
                    const active = tab === item.key;
                    return (
                      <Button
                        key={item.key}
                        onClick={() => setTab(item.key)}
                        variant={active ? 'contained' : 'outlined'}
                        startIcon={item.icon}
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          fontWeight: 600,
                          px: 2,
                          ...(active
                            ? { bgcolor: color, '&:hover': { bgcolor: color } }
                            : {
                                color,
                                borderColor: alpha(color, 0.4),
                                '&:hover': { borderColor: color, bgcolor: alpha(color, 0.08) },
                              }),
                        }}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Box>

                <Box p={3}>
                  {/* ——— Today's Roster ——— */}
                  {tab === 0 && (
                    <Box>
                      {rosterLoading ? (
                        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                      ) : roster.length === 0 ? (
                        <EmptyState
                          icon={DaycareIcon}
                          title="No students enrolled in daycare."
                          description="Enroll students in the Enrollments tab to get started."
                          actionLabel="Go to Enrollments"
                          onAction={() => setTab(3)}
                        />
                      ) : (
                        <Grid container spacing={2}>
                          {roster.map(r => (
                            <Grid item xs={12} sm={6} md={4} key={r.student_id}>
                              <Card
                                elevation={0}
                                sx={{
                                  border: '2px solid',
                                  borderColor: r.is_checked_in ? 'success.main' : 'grey.200',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <CardContent sx={{ pb: 1 }}>
                                  <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                    <Badge badgeContent={r.is_checked_in ? '✓' : null} color="success" overlap="circular">
                                      <Avatar sx={{ bgcolor: r.is_checked_in ? 'success.main' : 'grey.300' }}>
                                        {r.student_name?.[0]?.toUpperCase()}
                                      </Avatar>
                                    </Badge>
                                    <Box>
                                      <Typography variant="subtitle2" fontWeight={700}>{r.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">{r.admission_number}</Typography>
                                    </Box>
                                  </Box>
                                  {r.class_name && <Chip label={r.class_name} size="small" sx={{ mb: 1 }} />}
                                  {r.is_checked_in && r.open_session && (
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                      <ClockIcon fontSize="small" color="success" />
                                      <Typography variant="body2" color="success.main" fontWeight={600}>
                                        In since {fmtTime(r.open_session.check_in)} ({elapsed(r.open_session.check_in)})
                                      </Typography>
                                    </Box>
                                  )}
                                </CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, pb: 1.5 }}>
                                  {r.is_checked_in ? (
                                    <>
                                      <Button
                                        variant="contained" color="warning"
                                        startIcon={<CheckOutIcon />}
                                        onClick={() => handleCheckOut(r.open_session.id)}
                                        sx={{ flex: 1 }}
                                      >
                                        Check Out
                                      </Button>
                                      <Tooltip title="Edit session times">
                                        <IconButton size="small" onClick={() => openEdit(r.open_session)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Button
                                      variant="contained" color="success"
                                      startIcon={<CheckInIcon />}
                                      onClick={() => handleCheckIn(r.student_id)}
                                      sx={{ flex: 1 }}
                                    >
                                      Check In
                                    </Button>
                                  )}
                                  <Tooltip title="Notify parent">
                                    <IconButton size="small" onClick={() => openNotify(r)} sx={{ color: 'primary.main' }}>
                                      <NotifyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* ——— Care Notes ——— */}
                  {tab === 1 && (
                    <Box>
                      {notesLoading ? (
                        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                      ) : careNotes.length === 0 ? (
                        <EmptyState
                          icon={NoteIcon}
                          title="No care notes for daycare students today."
                        />
                      ) : (
                        <Grid container spacing={2}>
                          {careNotes.map(note => (
                            <Grid item xs={12} sm={6} key={note.id}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  border: '1px solid',
                                  borderColor: note.priority === 'urgent' ? 'error.light' : note.priority === 'high' ? 'warning.light' : 'grey.200',
                                  bgcolor: note.priority === 'urgent' ? 'error.50' : note.priority === 'high' ? 'warning.50' : '#fff',
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.light' }}>
                                      {note.student_name?.[0]?.toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" fontWeight={700}>{note.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">{note.parent_name}</Typography>
                                    </Box>
                                  </Box>
                                  <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                                    <Chip label={note.priority} size="small" color={PRIORITY_COLOR[note.priority]} icon={PRIORITY_ICON[note.priority]} />
                                    <Chip label={note.status} size="small" color={STATUS_COLOR[note.status]} />
                                  </Box>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'grey.800', lineHeight: 1.5 }}>
                                  {note.note_text}
                                </Typography>
                                {note.status === 'completed' && note.completion_remark && (
                                  <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={600}>Remark: </Typography>
                                    <Typography variant="caption" color="success.dark">{note.completion_remark}</Typography>
                                  </Box>
                                )}
                                <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5} flexWrap="wrap">
                                  {note.status === 'pending' && (
                                    <Button
                                      variant="outlined"
                                      startIcon={noteActionLoading === note.id + '_ack' ? <CircularProgress size={14} /> : <AcknowledgeIcon />}
                                      onClick={() => handleAcknowledge(note)}
                                      disabled={!!noteActionLoading}
                                    >
                                      Acknowledge
                                    </Button>
                                  )}
                                  {(note.status === 'pending' || note.status === 'acknowledged') && (
                                    <Button
                                      variant="contained" color="success"
                                      startIcon={noteActionLoading === note.id + '_complete' ? <CircularProgress size={14} color="inherit" /> : <CompleteIcon />}
                                      onClick={() => openComplete(note)}
                                      disabled={!!noteActionLoading}
                                    >
                                      Mark Completed
                                    </Button>
                                  )}
                                  <Button
                                    variant="outlined" startIcon={<NotifyIcon />}
                                    onClick={() => openNotify({ student_id: note.student_id, student_name: note.student_name })}
                                  >
                                    Notify Parent
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* ——— Daily Notes ——— */}
                  {tab === 2 && (
                    <Box>
                      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle1" fontWeight={700} mb={2}>
                          {dailyEditId ? 'Edit Daily Update' : 'Post Daily Update'}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select fullWidth label="Student" value={dailyForm.student_id}
                              onChange={e => setDailyForm(f => ({ ...f, student_id: e.target.value }))}
                              disabled={!!dailyEditId}
                            >
                              {roster.map(r => (
                                <MenuItem key={r.student_id} value={r.student_id}>
                                  {r.student_name} ({r.admission_number})
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth type="date" label="Date" value={dailyForm.update_date}
                              onChange={e => setDailyForm(f => ({ ...f, update_date: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth multiline minRows={3} label="Update / Notes *"
                              placeholder="Describe the child's day, activities, observations…"
                              value={dailyForm.content}
                              onChange={e => setDailyForm(f => ({ ...f, content: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth label="Meals" placeholder="e.g. Ate lunch well, had snack"
                              value={dailyForm.meals} onChange={e => setDailyForm(f => ({ ...f, meals: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth label="Nap" placeholder="e.g. 1 hour nap at 1 PM"
                              value={dailyForm.nap} onChange={e => setDailyForm(f => ({ ...f, nap: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select fullWidth label="Mood" value={dailyForm.mood}
                              onChange={e => setDailyForm(f => ({ ...f, mood: e.target.value }))}
                            >
                              <MenuItem value="">— Not specified —</MenuItem>
                              {MOODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth label="Activities" placeholder="e.g. Art, outdoor play, storytime"
                              value={dailyForm.activities} onChange={e => setDailyForm(f => ({ ...f, activities: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" gap={1}>
                              <Button
                                variant="contained" startIcon={<SendIcon />}
                                onClick={handleDailySubmit} disabled={dailySaving}
                              >
                                {dailySaving ? 'Saving…' : dailyEditId ? 'Save Changes' : 'Post Update'}
                              </Button>
                              {dailyEditId && (
                                <Button variant="outlined" onClick={() => { setDailyEditId(null); setDailyForm(EMPTY_DAILY_FORM); }}>
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>

                      {dailyNotesLoading ? (
                        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                      ) : dailyNotes.length === 0 ? (
                        <EmptyState
                          icon={DailyNotesIcon}
                          title="No daily diary entries for daycare students yet."
                        />
                      ) : (
                        <Grid container spacing={2}>
                          {dailyNotes.map(note => (
                            <Grid item xs={12} sm={6} key={note.id}>
                              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.light' }}>
                                      {note.student_name?.[0]?.toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" fontWeight={700}>{note.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">{fmtDate(note.update_date)} · {note.posted_by_name}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'grey.800', lineHeight: 1.5, mb: 1 }}>
                                  {note.content}
                                </Typography>
                                <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                                  {note.meals && <Chip size="small" label={`Meals: ${note.meals}`} />}
                                  {note.nap && <Chip size="small" label={`Nap: ${note.nap}`} />}
                                  {note.mood && <Chip size="small" label={`Mood: ${note.mood}`} />}
                                  {note.activities && <Chip size="small" label={`Activities: ${note.activities}`} />}
                                </Box>
                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => openDailyEdit(note)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => setDailyDeleteId(note.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* ——— Settings ——— */}
                  {tab === 6 && (
                    <Grid container spacing={3} maxWidth={520}>
                      <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={600} mb={0.5}>Daycare Configuration</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Set the hourly rate, enable/disable the module, and assign a teacher to manage daily daycare operations.
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Hourly Rate (₹)"
                          type="number"
                          fullWidth
                          value={settings.hourly_rate}
                          onChange={e => setSettings(s => ({ ...s, hourly_rate: parseFloat(e.target.value) || 0 }))}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          helperText="Rate is charged per hour (rounded up per session)"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="daycare-teacher-label">Assigned Daycare Teacher</InputLabel>
                          <Select
                            labelId="daycare-teacher-label"
                            label="Assigned Daycare Teacher"
                            value={settings.assigned_teacher_id || ''}
                            onChange={e => setSettings(s => ({ ...s, assigned_teacher_id: e.target.value }))}
                            startAdornment={<PersonIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary', fontSize: 20 }} />}
                          >
                            <MenuItem value=""><em>None (unassigned)</em></MenuItem>
                            {teachers.map(t => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name || t.user?.name} — {t.employee_id}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {settings.teacher_name && (
                          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                            Currently assigned: {settings.teacher_name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.is_enabled}
                              onChange={e => setSettings(s => ({ ...s, is_enabled: e.target.checked }))}
                            />
                          }
                          label="Daycare module enabled"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={handleSaveSettings}
                          disabled={settingsLoading}
                        >
                          {settingsLoading ? 'Saving…' : 'Save Settings'}
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {/* ——— Enrollments ——— */}
                  {tab === 3 && (
                    <Box>
                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
                        <TextField
                          size="small"
                          placeholder="Search students…"
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                        />
                        <Button variant="contained" startIcon={<EnrollIcon />} onClick={() => setEnrollDialog(true)}>
                          Enroll Student
                        </Button>
                      </Box>
                      {filteredEnrollments.length === 0 ? (
                        <EmptyState
                          icon={EnrollIcon}
                          title="No enrollments found."
                          description={search ? 'Try a different search.' : undefined}
                          variant={search ? 'no-results' : 'empty'}
                          actionLabel={!search ? 'Enroll Student' : undefined}
                          onAction={!search ? () => setEnrollDialog(true) : undefined}
                        />
                      ) : (
                        <ResponsiveDataView
                          rows={filteredEnrollments}
                          keyExtractor={(e) => e.id}
                          renderCard={(e) => (
                            <Card variant="outlined" sx={{ mb: 1.5 }}>
                              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
                                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.85rem' }}>
                                      {e.student_name?.[0]?.toUpperCase()}
                                    </Avatar>
                                    <Box minWidth={0}>
                                      <Typography variant="body2" fontWeight={600} noWrap>{e.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {e.class_name || 'No class'} · {fmtDate(e.enrolled_at)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Tooltip title={e.is_active ? 'Deactivate' : 'Activate'}>
                                    <Switch
                                      checked={e.is_active}
                                      size="small"
                                      onChange={() => handleToggleEnrollment(e.id, e.is_active)}
                                    />
                                  </Tooltip>
                                </Box>
                                <Chip
                                  label={e.is_active ? 'Active' : 'Inactive'}
                                  color={e.is_active ? 'success' : 'default'}
                                  size="small"
                                  icon={e.is_active ? <ActiveIcon /> : <InactiveIcon />}
                                  sx={{ mt: 1 }}
                                />
                              </CardContent>
                            </Card>
                          )}
                          table={
                            <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Student</strong></TableCell>
                                    <TableCell><strong>Class</strong></TableCell>
                                    <TableCell><strong>Enrolled</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Action</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filteredEnrollments.map(e => (
                                    <TableRow key={e.id} hover>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.85rem' }}>
                                            {e.student_name?.[0]?.toUpperCase()}
                                          </Avatar>
                                          <Box>
                                            <Typography variant="body2" fontWeight={600}>{e.student_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{e.admission_number}</Typography>
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell>{e.class_name || '—'}</TableCell>
                                      <TableCell>{fmtDate(e.enrolled_at)}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={e.is_active ? 'Active' : 'Inactive'}
                                          color={e.is_active ? 'success' : 'default'}
                                          size="small"
                                          icon={e.is_active ? <ActiveIcon /> : <InactiveIcon />}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Tooltip title={e.is_active ? 'Deactivate' : 'Activate'}>
                                          <Switch
                                            checked={e.is_active}
                                            size="small"
                                            onChange={() => handleToggleEnrollment(e.id, e.is_active)}
                                          />
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          }
                        />
                      )}
                    </Box>
                  )}

                  {/* ——— Daycare Student Profile ——— */}
                  {tab === 4 && (
                    <Box>
                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
                        <TextField
                          size="small"
                          placeholder="Search students…"
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                        />
                      </Box>
                      {filteredEnrollments.length === 0 ? (
                        <EmptyState
                          icon={PersonIcon}
                          title="No daycare-enrolled students found."
                          description={search ? 'Try a different search.' : undefined}
                          variant={search ? 'no-results' : 'empty'}
                        />
                      ) : (
                        <Grid container spacing={2}>
                          {filteredEnrollments.map(e => (
                            <Grid item xs={12} sm={6} md={4} key={e.id}>
                              <Card
                                elevation={0}
                                sx={{ border: '2px solid', borderColor: 'grey.200', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                                onClick={() => navigate(`/principal/daycare/students/${e.student_id}`)}
                              >
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                                    {e.student_name?.[0]?.toUpperCase()}
                                  </Avatar>
                                  <Box flex={1}>
                                    <Typography variant="subtitle2" fontWeight={700}>{e.student_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{e.admission_number}</Typography>
                                    {e.class_name && <Chip label={e.class_name} size="small" sx={{ mt: 0.5 }} />}
                                  </Box>
                                  <Chip
                                    label={e.is_active ? 'Active' : 'Inactive'}
                                    color={e.is_active ? 'success' : 'default'}
                                    size="small"
                                  />
                                  <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* ——— Outstanding Dues ——— */}
                  {tab === 5 && (
                    <Box>
                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly totals from completed sessions.
                        </Typography>
                        <Button
                          variant="contained" color="error" startIcon={<MoneyIcon />}
                          onClick={() => navigate('/principal/daycare-dues')}
                        >
                          Manage Dues &amp; Record Payments
                        </Button>
                      </Box>
                      {dues.length === 0 ? (
                        <EmptyState
                          icon={MoneyIcon}
                          title="No daycare dues on record."
                        />
                      ) : (
                        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Student</strong></TableCell>
                                <TableCell><strong>Class</strong></TableCell>
                                <TableCell><strong>Month</strong></TableCell>
                                <TableCell align="right"><strong>Amount</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dues.map((d, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{d.student_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{d.admission_number}</Typography>
                                  </TableCell>
                                  <TableCell>{d.class_name || '—'}</TableCell>
                                  <TableCell>
                                    {new Date(d.period_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight={700} color="primary.main">{fmt(d.month_total)}</Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </>
          )}
        </Box>

      {/* Edit Session Dialog */}
      <MobileFormDialog
        open={!!editDialog}
        onClose={() => setEditDialog(null)}
        title="Edit Session Times"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={() => setEditDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>Save</Button>
          </>
        }
      >
        <TextField
          label="Check In" type="datetime-local" fullWidth sx={{ mb: 2, mt: 1 }}
          value={editForm.check_in}
          onChange={e => setEditForm(f => ({ ...f, check_in: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Check Out (leave blank if still in)" type="datetime-local" fullWidth
          value={editForm.check_out}
          onChange={e => setEditForm(f => ({ ...f, check_out: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </MobileFormDialog>

      {/* Notify Parent Dialog */}
      <MobileFormDialog
        open={!!notifyDialog}
        onClose={() => setNotifyDialog(null)}
        title="Notify Parent"
        actions={
          <>
            <Button onClick={() => setNotifyDialog(null)} disabled={notifySending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={notifySending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSendNotification}
              disabled={!notifyMsg.trim() || notifySending}
            >
              {notifySending ? 'Sending…' : 'Send'}
            </Button>
          </>
        }
      >
        {notifyDialog && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Send a message to the parent of <strong>{notifyDialog.student_name}</strong>.
            They will receive an in-app notification and an email.
          </Typography>
        )}
        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          label="Message to parent"
          placeholder="e.g. Your child had a great day! They are ready for pickup at 5 PM."
          value={notifyMsg}
          onChange={e => setNotifyMsg(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={`${notifyMsg.length}/500`}
        />
      </MobileFormDialog>

      {/* Complete Care Note Dialog */}
      <MobileFormDialog
        open={!!completeDialog}
        onClose={() => setCompleteDialog(null)}
        title="Mark as Completed"
        actions={
          <>
            <Button onClick={() => setCompleteDialog(null)} disabled={!!noteActionLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleCompleteSubmit}
              disabled={!!noteActionLoading}
              startIcon={noteActionLoading ? <CircularProgress size={14} color="inherit" /> : <CompleteConfirmIcon />}
            >
              Complete &amp; Notify Parent
            </Button>
          </>
        }
      >
        {completeDialog && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Note for {completeDialog.note.student_name}:</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{completeDialog.note.note_text}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Completion remark (optional)"
          placeholder="E.g., Gave cough syrup at 11 AM and 4 PM"
          value={completionRemark}
          onChange={e => setCompletionRemark(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The parent will receive an email and in-app notification when you mark this as completed.
        </Typography>
      </MobileFormDialog>

      {/* Enroll Student Dialog */}
      <Dialog open={enrollDialog} onClose={() => setEnrollDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enroll Student in Daycare</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus size="small" fullWidth sx={{ mt: 1, mb: 2 }}
            placeholder="Search students…"
            value={enrollSearch}
            onChange={e => setEnrollSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5 }} /> }}
          />
          {unenrolledStudents.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {enrollSearch ? 'No matches.' : 'All students are already enrolled.'}
            </Typography>
          ) : (
            <Box maxHeight={320} overflow="auto">
              {unenrolledStudents.map(s => (
                <Box
                  key={s.id}
                  display="flex" alignItems="center" justifyContent="space-between"
                  px={1} py={1} borderBottom="1px solid #f0f0f0"
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.light', fontSize: '0.85rem' }}>
                      {s.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.admission_number}</Typography>
                    </Box>
                  </Box>
                  <Button variant="outlined" onClick={() => handleEnroll(s.id)}>Enroll</Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Daily Update Confirmation */}
      <Dialog open={!!dailyDeleteId} onClose={() => setDailyDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Daily Update?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDailyDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
